import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { Search, User, Phone, Calendar, ExternalLink, Filter, X, Trash2, Shuffle, QrCode } from 'lucide-react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import ClientDetailsModal from './ClientDetailsModal';

// ─── Animations ────────────────────────────────────────────────────────────────

const shimmer = keyframes`
  0% { background-position: -600px 0; }
  100% { background-position: 600px 0; }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(4px); }
  to { opacity: 1; transform: translateY(0); }
`;

// ─── Layout ─────────────────────────────────────────────────────────────────────

const Container = styled.div`
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.06), 0 4px 16px rgba(0,0,0,0.04);
  border: 1px solid #e8edf3;
  overflow: hidden;
`;

const Header = styled.div`
  padding: 18px 24px;
  border-bottom: 1px solid #f1f5f9;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  background: #ffffff;
`;

const Title = styled.h2`
  font-size: 15px;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;

  span.count {
    font-size: 12px;
    font-weight: 600;
    color: #6366f1;
    background: #eef2ff;
    padding: 2px 8px;
    border-radius: 999px;
  }
`;

const TitleIcon = styled.div`
  width: 30px;
  height: 30px;
  border-radius: 8px;
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const ControlsRow = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  align-items: center;
`;

// ─── Search ──────────────────────────────────────────────────────────────────────

const SearchWrap = styled.div`
  display: flex;
  align-items: center;
  background: #f8fafc;
  border: 1.5px solid #e2e8f0;
  border-radius: 10px;
  padding: 0 12px;
  min-width: 240px;
  height: 38px;
  gap: 8px;
  transition: border-color 0.15s, box-shadow 0.15s;

  &:focus-within {
    border-color: #6366f1;
    box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.12);
    background: #fff;
  }
`;

const SearchInput = styled.input`
  border: none;
  background: transparent;
  outline: none;
  flex: 1;
  font-size: 13.5px;
  color: #1e293b;

  &::placeholder { color: #94a3b8; }
`;

const ClearBtn = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: #94a3b8;
  display: flex;
  align-items: center;
  transition: color 0.15s;
  &:hover { color: #475569; }
`;

const FilterWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const FilterSelect = styled.select`
  height: 38px;
  padding: 0 12px;
  border-radius: 10px;
  border: 1.5px solid #e2e8f0;
  background: #f8fafc;
  font-size: 13.5px;
  color: #475569;
  outline: none;
  cursor: pointer;
  transition: border-color 0.15s;

  &:focus {
    border-color: #6366f1;
    background: #fff;
  }
`;

// ─── Table ───────────────────────────────────────────────────────────────────────

const TableWrap = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  text-align: left;
  padding: 11px 20px;
  background: #f8fafc;
  color: #94a3b8;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  border-bottom: 1px solid #f1f5f9;
  white-space: nowrap;
`;

const Td = styled.td`
  padding: 14px 20px;
  border-bottom: 1px solid #f8fafc;
  color: #334155;
  font-size: 13.5px;
  vertical-align: middle;
`;

const ClientRow = styled.tr`
  cursor: pointer;
  transition: background 0.12s;
  animation: ${fadeIn} 0.2s ease both;
  animation-delay: ${props => props.delay || '0ms'};

  &:hover {
    background: #fafbff;
  }

  &:hover td:first-child {
    box-shadow: inset 3px 0 0 #6366f1;
  }

  &:last-child td {
    border-bottom: none;
  }
`;

// ─── Avatar ───────────────────────────────────────────────────────────────────────

const AVATAR_COLORS = [
  ['#eef2ff', '#4f46e5'],
  ['#fdf4ff', '#9333ea'],
  ['#f0fdf4', '#16a34a'],
  ['#fff7ed', '#ea580c'],
  ['#eff6ff', '#2563eb'],
  ['#fef2f2', '#dc2626'],
  ['#f0fdfa', '#0d9488'],
];

const Avatar = styled.div`
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: ${props => props.bg};
  color: ${props => props.fg};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 13px;
  flex-shrink: 0;
  letter-spacing: 0.3px;
`;

function getAvatarColors(name = '') {
  const idx = (name.charCodeAt(0) || 0) % AVATAR_COLORS.length;
  return AVATAR_COLORS[idx];
}

function getInitials(firstName, lastName) {
  const f = (firstName || '').charAt(0).toUpperCase();
  const l = (lastName || '').charAt(0).toUpperCase();
  return f + (l || '');
}

// ─── Badges ───────────────────────────────────────────────────────────────────────

const VisitBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;

  ${props => {
    const n = props.count || 0;
    if (n >= 10) return 'background:#eef2ff; color:#4f46e5;';
    if (n >= 5)  return 'background:#eff6ff; color:#2563eb;';
    if (n >= 2)  return 'background:#f0fdf4; color:#16a34a;';
    return               'background:#f8fafc; color:#64748b;';
  }}
`;

const StoreBadge = styled.span`
  font-size: 12.5px;
  color: #475569;
  font-weight: 500;
`;

const MutedText = styled.span`
  color: #cbd5e1;
  font-size: 12px;
`;

// ─── Action button ────────────────────────────────────────────────────────────────

const ActionBtn = styled.button`
  width: 34px;
  height: 34px;
  border: 1.5px solid #e0e7ff;
  border-radius: 9px;
  background: #f5f3ff;
  color: #6366f1;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;

  &:hover {
    background: #6366f1;
    border-color: #6366f1;
    color: #fff;
    transform: translateY(-1px);
    box-shadow: 0 4px 10px rgba(99,102,241,0.25);
  }

  &:active {
    transform: translateY(0);
    box-shadow: none;
  }
`;

// ─── Skeleton ─────────────────────────────────────────────────────────────────────

const SkeletonBase = styled.div`
  background: linear-gradient(90deg, #f1f5f9 25%, #e8edf3 50%, #f1f5f9 75%);
  background-size: 600px 100%;
  animation: ${shimmer} 1.4s infinite linear;
  border-radius: 6px;
`;

const SkeletonRow = ({ cols = 7 }) => (
  <tr>
    {Array.from({ length: cols }).map((_, i) => (
      <Td key={i} style={{ padding: '14px 20px' }}>
        <SkeletonBase style={{ height: 14, width: i === 0 ? 140 : i === 6 ? 34 : 80, borderRadius: i === 0 ? 20 : 6 }} />
      </Td>
    ))}
  </tr>
);

// ─── Empty state ──────────────────────────────────────────────────────────────────

const EmptyState = styled.div`
  padding: 60px 20px;
  text-align: center;
  color: #94a3b8;

  svg { margin-bottom: 12px; opacity: 0.4; }

  p.title {
    font-size: 15px;
    font-weight: 600;
    color: #64748b;
    margin: 0 0 6px;
  }

  p.sub {
    font-size: 13px;
    margin: 0;
  }
`;

// ─── Helpers ──────────────────────────────────────────────────────────────────────

const formatDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toLocaleDateString();
};

const formatAge = (client) => {
  if (client.age) return `${client.age}y`;
  if (!client.birthday) return (client.ageGroup || '—').replace(/_/g, ' ');
  const bd = new Date(client.birthday);
  if (Number.isNaN(bd.getTime())) return (client.ageGroup || '—').replace(/_/g, ' ');
  const today = new Date();
  let age = today.getFullYear() - bd.getFullYear();
  const md = today.getMonth() - bd.getMonth();
  if (md < 0 || (md === 0 && today.getDate() < bd.getDate())) age -= 1;
  return age >= 0 ? `${age}y` : '—';
};

// ─── Component ────────────────────────────────────────────────────────────────────

const ClientList = () => {
  const { t } = useTranslation();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedClientKey, setSelectedClientKey] = useState(null);
  const [stores, setStores] = useState([]);
  const [selectedStore, setSelectedStore] = useState('');
  // 'clients'   — ordered at least once
  // 'potential' — visited and left contact details but never ordered
  // 'cross'     — admin only: the same person registered at 2+ branches
  const [view, setView] = useState('clients');
  const [userRole, setUserRole] = useState('');

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    setUserRole(user.role);
    if (user.role === 'admin' || user.role === 'advisor') fetchStores();
    fetchClients();
  }, []);

  useEffect(() => {
    if (userRole) fetchClients();
  }, [selectedStore, view]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchStores = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get('/api/stores', { headers });
      const list = Array.isArray(res.data) ? res.data : res.data?.stores || [];
      setStores(list);
    } catch (err) {
      console.error('Error fetching stores:', err);
    }
  };

  const fetchClients = async () => {
    setLoading(true);
    try {
      let url = '/api/visitors/clients';
      const params = [];
      // Browsing the client book is scoped to your own store. `scope=store`
      // makes the server resolve the store from the token, so it can't be
      // pointed at another branch. Admins keep the tenant-wide store picker.
      // The returning-client lookup during a sale deliberately does NOT send
      // this — a client who first visited another branch must still be findable
      // so their history follows them instead of being re-registered.
      const role = JSON.parse(localStorage.getItem('user') || '{}').role;
      const isTenantWide = role === 'admin' || role === 'super_admin' || role === 'advisor';
      if (selectedStore) params.push(`storeId=${selectedStore}`);
      else if (!isTenantWide) params.push('scope=store');
      if (view === 'cross' && isTenantWide) params.push('crossStore=1');
      else params.push(`type=${view === 'potential' ? 'potential' : 'clients'}`);
      if (search) params.push(`search=${encodeURIComponent(search)}`);
      if (params.length) url += `?${params.join('&')}`;
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(url, { headers });
      setClients(res.data);
    } catch (err) {
      console.error('Failed to fetch clients', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => fetchClients(), 500);
    return () => clearTimeout(timer);
  }, [search]);

  const getClientDetailKey = (client) => {
    if (client.clientKey) return String(client.clientKey);
    if (client.visitorId) return String(client.visitorId);
    if (client._id) return String(client._id);
    if (client.phone) return String(client.phone);
    return null;
  };

  const handleRowClick = (client) => {
    const key = getClientDetailKey(client);
    if (key) setSelectedClientKey(key);
  };

  // Prints the client's QR card on the same 40mm × 20mm label as the frame
  // stickers, via the dedicated print page the order-confirmation screen also
  // uses — so a card reprinted from here is identical to the original.
  const handlePrintClientCard = (client, e) => {
    e.stopPropagation();
    const code = client.clientCode || client.legacyClientCode;
    if (!code) return;
    const name = `${client.firstName || ''} ${client.lastName || ''}`.trim();
    const url = `/print-label/${encodeURIComponent(code)}?name=${encodeURIComponent(name)}`;
    const win = window.open(url, 'client-label-print', 'width=900,height=700,left=200,top=80');
    if (!win) window.alert(t('clients.printCardPopupBlocked'));
  };

  // Admin-only: permanently delete a client and all of their prescriptions/orders.
  const handleDeleteClient = async (client, e) => {
    e.stopPropagation();
    // Send the grouped client key, not a single visit's visitor id — one row
    // here can cover several visits, and deleting just one leaves the rest.
    const identifier = client.clientKey || client.visitorId || client._id;
    if (!identifier) return;
    const name = `${client.firstName || ''} ${client.lastName || ''}`.trim() || 'this client';
    if (!window.confirm(`Delete ${name} and all their prescriptions and orders? This cannot be undone.`)) return;
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.delete(`/api/visitors/${encodeURIComponent(identifier)}`, { headers });
      setClients(prev => prev.filter(c => (c.clientKey || c.visitorId || c._id) !== identifier));
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to delete client');
    }
  };

  return (
    <Container>
      <Header>
        <Title>
          <TitleIcon><User size={15} color="#fff" /></TitleIcon>
          {t('clients.directory')}
          {!loading && <span className="count">{clients.length}</span>}
        </Title>

        <ControlsRow>
          {/* Clients bought; Potential Clients visited and left details but
              haven't bought yet. Cross-Store is admin-only — a single store is
              deliberately not told its client shops at another branch. */}
          <FilterWrap>
            <Filter size={14} color="#94a3b8" />
            <FilterSelect value={view} onChange={e => setView(e.target.value)}>
              <option value="clients">{t('clients.viewClients')}</option>
              <option value="potential">{t('clients.viewPotential')}</option>
              {userRole === 'admin' && <option value="cross">{t('clients.viewCrossStore')}</option>}
            </FilterSelect>
          </FilterWrap>

          {userRole === 'admin' && (
            <FilterWrap>
              <Filter size={14} color="#94a3b8" />
              <FilterSelect value={selectedStore} onChange={e => setSelectedStore(e.target.value)}>
                <option value="">{t('clients.allStores')}</option>
                {stores.map(s => <option key={s._id} value={s._id}>{s.name}</option>)}
              </FilterSelect>
            </FilterWrap>
          )}

          <SearchWrap>
            <Search size={14} color="#94a3b8" />
            <SearchInput
              placeholder={t('clients.searchPlaceholder')}
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            {search && (
              <ClearBtn onClick={() => setSearch('')} aria-label={t('clients.clearSearch')}>
                <X size={14} />
              </ClearBtn>
            )}
          </SearchWrap>
        </ControlsRow>
      </Header>

      <TableWrap>
        <Table>
          <thead>
            <tr>
              <Th>{t('clients.colClient')}</Th>
              <Th>{t('clients.colAge')}</Th>
              <Th>{t('clients.colContact')}</Th>
              <Th>{t('clients.colLastVisit')}</Th>
              <Th>{t('clients.colStore')}</Th>
              <Th>{t('clients.colVisits')}</Th>
              <Th></Th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              Array.from({ length: 6 }).map((_, i) => <SkeletonRow key={i} />)
            ) : clients.length === 0 ? (
              <tr>
                <Td colSpan="7" style={{ padding: 0, border: 'none' }}>
                  <EmptyState>
                    <User size={40} />
                    <p className="title">{view === 'potential' ? t('clients.noPotential') : t('clients.noClients')}</p>
                    <p className="sub">
                      {search
                        ? t('clients.noMatch')
                        : view === 'potential' ? t('clients.noPotentialYet') : t('clients.noClientsYet')}
                    </p>
                  </EmptyState>
                </Td>
              </tr>
            ) : (
              clients.map((client, i) => {
                const name = `${client.firstName || ''} ${client.lastName || ''}`.trim();
                const [bg, fg] = getAvatarColors(name);
                const initials = getInitials(client.firstName, client.lastName);
                const visitCount = client.visitCount ?? 0;
                const lastVisit = formatDate(client.lastVisit);

                return (
                  <ClientRow
                    // rowKey is (person + store): the same person can legitimately
                    // appear twice, once per branch, so clientKey alone collides.
                    key={client.rowKey || getClientDetailKey(client) || i}
                    delay={`${i * 30}ms`}
                    onClick={() => handleRowClick(client)}
                  >
                    {/* Client name + avatar */}
                    <Td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <Avatar bg={bg} fg={fg}>{initials || '?'}</Avatar>
                        <div>
                          <div style={{ fontWeight: 600, color: '#0f172a', fontSize: 14, lineHeight: 1.3 }}>
                            {name || 'Unknown'}
                          </div>
                          {client.email && (
                            <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>
                              {client.email}
                            </div>
                          )}
                        </div>
                      </div>
                    </Td>

                    {/* Age */}
                    <Td style={{ color: '#64748b', fontWeight: 500 }}>
                      {formatAge(client)}
                    </Td>

                    {/* Contact */}
                    <Td>
                      {client.phone ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#334155' }}>
                          <Phone size={13} color="#94a3b8" />
                          {client.phone}
                        </div>
                      ) : (
                        <MutedText>No phone</MutedText>
                      )}
                    </Td>

                    {/* Last visit */}
                    <Td>
                      {lastVisit ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#475569' }}>
                          <Calendar size={13} color="#94a3b8" />
                          {lastVisit}
                        </div>
                      ) : (
                        <MutedText>—</MutedText>
                      )}
                    </Td>

                    {/* Store this person is a client OF. `otherStores` is only
                        sent to admins — it's the cross-store view a single
                        store is deliberately not given. */}
                    <Td>
                      {client.store?.name
                        ? <StoreBadge>{client.store.name}</StoreBadge>
                        : <MutedText>—</MutedText>}
                      {client.otherStores?.length > 0 && (
                        <div style={{ fontSize: 11, color: '#b45309', marginTop: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
                          <Shuffle size={11} />
                          {t('clients.alsoAt')} {client.otherStores.map(s => s?.name).filter(Boolean).join(', ')}
                        </div>
                      )}
                    </Td>

                    {/* Visit count */}
                    <Td>
                      <VisitBadge count={visitCount}>
                        {visitCount} {visitCount === 1 ? t('clients.visit') : t('clients.visits')}
                      </VisitBadge>
                    </Td>

                    {/* Action */}
                    <Td style={{ paddingRight: 20 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'flex-end' }}>
                        {(client.clientCode || client.legacyClientCode) && (
                          <ActionBtn
                            type="button"
                            aria-label={`Print QR card for ${client.firstName || 'client'}`}
                            title={t('clients.printCard')}
                            onClick={e => handlePrintClientCard(client, e)}
                          >
                            <QrCode size={15} />
                          </ActionBtn>
                        )}
                        <ActionBtn
                          type="button"
                          aria-label={`View ${client.firstName || 'client'} details`}
                          onClick={e => { e.stopPropagation(); handleRowClick(client); }}
                        >
                          <ExternalLink size={15} />
                        </ActionBtn>
                        {userRole === 'admin' && (
                          <ActionBtn
                            type="button"
                            aria-label={`Delete ${client.firstName || 'client'}`}
                            title="Delete client"
                            onClick={e => handleDeleteClient(client, e)}
                            style={{ color: '#dc2626', borderColor: '#fecaca' }}
                          >
                            <Trash2 size={15} />
                          </ActionBtn>
                        )}
                      </div>
                    </Td>
                  </ClientRow>
                );
              })
            )}
          </tbody>
        </Table>
      </TableWrap>

      {selectedClientKey && (
        <ClientDetailsModal
          clientIdentifier={selectedClientKey}
          onClose={() => setSelectedClientKey(null)}
        />
      )}
    </Container>
  );
};

export default ClientList;
